import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Typography,
  message
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { formatCents } from "../shared/money";
import { errorText } from "../api";
import * as api from "./api";

const LinesTable = ({ lines }) => {
  const { t } = useTranslation("accounting");
  return (
    // Journal lines (debit/credit) stay a grid on phones.
    <ResponsiveTable
      mobileLayout="table"
      className="acct-grid"
      size="small"
      pagination={false}
      rowKey={(_, i) => i}
      dataSource={lines}
      columns={[
        { title: t("Account"), dataIndex: "account" },
        { title: t("Text"), dataIndex: "text" },
        {
          title: t("Debit"),
          dataIndex: "debit",
          align: "end",
          render: v => (v ? <Amount cents={v} /> : "")
        },
        {
          title: t("Credit"),
          dataIndex: "credit",
          align: "end",
          render: v => (v ? <Amount cents={v} /> : "")
        }
      ]}
    />
  );
};

const TripCard = ({ rates, projects }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = v => ({
    ...v,
    start: v.start ? dayjs(v.start).format("YYYY-MM-DDTHH:mm:00") : "",
    end: v.end ? dayjs(v.end).format("YYYY-MM-DDTHH:mm:00") : "",
    date: v.end ? dayjs(v.end).format("YYYY-MM-DD") : ""
  });
  const run = async book => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const res = book ? await api.bookTrip(body(v)) : await api.previewTrip(body(v));
      if (book) {
        message.success(
          `${t("Trip booked")} (${t("Entry")} ${res.data.entry.entryNo}/${res.data.entry.year})`
        );
        setPreview(null);
        form.resetFields();
      } else setPreview(res.data);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  const countries = [
    { value: "DE", label: "Deutschland" },
    ...Object.entries(rates?.foreign || {}).map(([k, v]) => ({ value: k, label: v.name }))
  ];
  return (
    <Card title={t("Business trip")} size="small">
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          country: "DE",
          km: 0,
          overnightNights: 0,
          overnightTaxRate: 7,
          employee: false
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="start" label={t("Departure")} rules={[{ required: true }]}>
              <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="end" label={t("Return")} rules={[{ required: true }]}>
              <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="destination" label={t("Destination")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="country" label={t("Country")}>
              <Select showSearch optionFilterProp="label" options={countries} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="km" label={t("Kilometres (private car, 0,30 €/km)")}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="overnightNights" label={t("Nights (flat rate 20 €)")}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="overnightActual" label={t("Hotel bill (gross, replaces flat rate)")}>
              <MoneyInput />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="overnightTaxRate" label={t("Hotel VAT")}>
              <Select options={[7, 19, 0].map(r => ({ value: r, label: `${r} %` }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="paidFromAccount" label={t("Paid from account (number)")}>
              <Input placeholder="1200 / 1000" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="costCentre" label={t("Project")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={projects.map(p => ({ value: p.id, label: p.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="employee"
              label={t("Employee trip (paid out with wages)")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <div className="acct-actions">
          <Button onClick={() => run(false)} loading={busy}>
            {t("Preview")}
          </Button>
          <Button type="primary" onClick={() => run(true)} loading={busy} disabled={!preview}>
            {t("Book")}
          </Button>
        </div>
      </Form>
      {preview ? (
        <div style={{ marginBlockStart: 12 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4, xl: 2, xxl: 4 }}>
            <Descriptions.Item label={t("Per diem")}>
              <Amount cents={preview.breakdown.perDiem.total} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Kilometres")}>
              <Amount cents={preview.breakdown.kmAmount} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Overnight")}>
              <Amount
                cents={preview.breakdown.overnightPauschale || preview.breakdown.overnightActual}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t("Total")}>
              <Amount cents={preview.breakdown.total} />
            </Descriptions.Item>
          </Descriptions>
          <Typography.Text type="secondary" className="acct-wrap">
            {preview.breakdown.perDiem.days
              .map(d => `${d.date}: ${t(`perdiem_${d.kind}`)} ${formatCents(d.amount)}`)
              .join(" · ")}
          </Typography.Text>
          <LinesTable lines={preview.lines} />
        </div>
      ) : null}
    </Card>
  );
};

const EntertainmentCard = ({ projects }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = v => ({
    ...v,
    date: v.date ? dayjs(v.date).format("YYYY-MM-DD") : "",
    participants: String(v.participants || "")
      .split(/\n|,/)
      .map(s => s.trim())
      .filter(Boolean)
  });
  const run = async book => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const res = book
        ? await api.bookEntertainment(body(v))
        : await api.previewEntertainment(body(v));
      if (book) {
        message.success(
          `${t("Bewirtung booked")} (${t("Entry")} ${res.data.entry.entryNo}/${res.data.entry.year})`
        );
        setPreview(null);
        form.resetFields();
      } else setPreview(res.data);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t("Entertainment receipt (Bewirtung)")} size="small">
      <Form form={form} layout="vertical" initialValues={{ date: dayjs(), taxRate: 19, tip: 0 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="amount"
              label={t("Total paid (gross incl. tip)")}
              rules={[{ required: true }]}
            >
              <MoneyInput />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="tip" label={t("Tip")}>
              <MoneyInput />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="taxRate" label={t("VAT rate")}>
              <Select options={[19, 7, 0].map(r => ({ value: r, label: `${r} %` }))} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="place" label={t("Place (restaurant)")} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="occasion" label={t("Occasion")} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="participants"
          label={t("Participants (one per line)")}
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="paidFromAccount" label={t("Paid from account (number)")}>
              <Input placeholder="1200 / 1000" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="costCentre" label={t("Project")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={projects.map(p => ({ value: p.id, label: p.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <div className="acct-actions">
          <Button onClick={() => run(false)} loading={busy}>
            {t("Preview")}
          </Button>
          <Button type="primary" onClick={() => run(true)} loading={busy} disabled={!preview}>
            {t("Book")}
          </Button>
        </div>
      </Form>
      {preview ? (
        <div style={{ marginBlockStart: 12 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4, xl: 2, xxl: 4 }}>
            <Descriptions.Item label={t("Net")}>
              <Amount cents={preview.breakdown.net} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Input VAT")}>
              <Amount cents={preview.breakdown.tax} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Deductible (70 %)")}>
              <Amount cents={preview.breakdown.deductible} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Non-deductible (30 %)")}>
              <Amount cents={preview.breakdown.nonDeductible} />
            </Descriptions.Item>
          </Descriptions>
          <LinesTable lines={preview.lines} />
        </div>
      ) : null}
    </Card>
  );
};

const TravelTab = () => {
  const { t } = useTranslation("accounting");
  const [rates, setRates] = useState(null);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api
      .travelRates()
      .then(res => setRates(res.data))
      .catch(() => setRates(null));
    api
      .listProjects()
      .then(res => setProjects(res.data.projects || []))
      .catch(() => setProjects([]));
  }, []);
  return (
    <div>
      <Typography.Paragraph type="secondary">
        {t(
          "Per diems 2026: 14 € for more than 8 hours and for arrival/departure days, 28 € for full days; 0,30 € per km; 20 € overnight flat rate. Entertainment: 70 % deductible, 30 % not, full input VAT."
        )}
      </Typography.Paragraph>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <TripCard rates={rates} projects={projects} />
        </Col>
        <Col xs={24} xl={12}>
          <EntertainmentCard projects={projects} />
        </Col>
      </Row>
    </div>
  );
};

export default TravelTab;
